import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/data-display/page-header";
import { FilterBar } from "@/components/data-display/filter-bar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { LoadingState, LoadingTable, NotAvailableState } from "@/components/ui/system-state";
import { Table, TableWrapper } from "@/components/ui/table";
import { AdminConsoleFailureState } from "@/features/admin/admin-console-failure";
import {
  SUPPORT_REFERENCE_INPUT,
  fetchWalletAccessCheck,
  outcomeTone,
  searchSignInDiagnostics,
  type AdminConsoleFailure,
  type AdminTool,
  type SignInDiagnostics,
  type WalletAccessCheck,
} from "@/lib/admin-console-api";

/**
 * D3/D4 — Sign-in diagnostics (ALL-5777, spec: product-design/specs/04-governance-evidence/
 * admin-support-diagnostics-spec.md). Why a person could not sign in or open the
 * wallet page: the admin console's wallet-page access check
 * (GET /admin/api/access-diagnostics/wallet-page) and recent sign-in events
 * (GET /admin/api/auth-diagnostics). Read-only: tools that change access are
 * shown as guidance and point to the user's page, where role changes ask for
 * step-up.
 */

type Load<T> = { kind: "loading" } | { kind: "ready"; data: T } | { kind: "failed"; failure: AdminConsoleFailure };

const REASON_INPUT = /^[A-Za-z0-9_.:-]{1,96}$/;

function humanize(token: string | undefined): string {
  if (!token) return "—";
  const text = token.replace(/[_-]+/g, " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : token;
}

function formatTime(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

const CHECK_STATUS: Record<string, { label: string; tone: "success" | "warning" | "danger" | "neutral" }> = {
  ready: { label: "Working", tone: "success" },
  allowed: { label: "Working", tone: "success" },
  blocked: { label: "Blocked", tone: "danger" },
  failed: { label: "Blocked", tone: "danger" },
  degraded: { label: "Could not be checked", tone: "warning" },
  partially_observed: { label: "Partly confirmed", tone: "warning" },
};

const TOOL_TITLE: Record<string, string> = {
  refresh_login: "Ask the person to sign in again",
  scoped_auth_diagnostics_lookup: "Search sign-in events for their reference",
  gateway_wallet_read_probe: "Read-only wallet list check",
  role_mapping_repair: "Fix the person's role",
};

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-0.5 py-1.5 sm:grid-cols-[170px_1fr] sm:gap-3">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="min-w-0 break-words text-sm text-ink">{children}</dd>
    </div>
  );
}

function ToolItem({ tool }: { tool: AdminTool }) {
  return (
    <li className="flex flex-col gap-1 px-3 py-2.5" data-testid="admin-tool">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-semibold text-ink">{TOOL_TITLE[tool.id] ?? humanize(tool.id)}</span>
        <Badge variant={tool.mutatesAccess ? "warning" : "info"} size="sm">
          {tool.mutatesAccess ? "Changes access" : "Read-only"}
        </Badge>
      </div>
      {tool.action && <p className="text-xs text-muted">{tool.action}</p>}
      {tool.mutatesAccess && (
        <p className="text-xs text-muted">
          Change roles on the person's page in <Link to="/admin/users" className="text-primary hover:underline">Users</Link>. It asks
          you to confirm your identity first.
        </p>
      )}
    </li>
  );
}

function WalletAccessCard({ check }: { check: WalletAccessCheck | null }) {
  if (!check) {
    return (
      <NotAvailableState
        title="No wallet page access check was returned"
        description="The console answered without an access check. Try again later, or search sign-in events below."
      />
    );
  }
  const status = CHECK_STATUS[check.status] ?? { label: humanize(check.status), tone: "neutral" as const };
  return (
    <Card data-testid="wallet-access-check">
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle>Wallet page access</CardTitle>
            <p className="mt-1 text-sm text-muted">Whether people in your organization can open their wallets, and why not.</p>
          </div>
          <Badge variant={status.tone}>{status.label}</Badge>
        </div>
        <dl className="mt-3 divide-y divide-line">
          <Row label="Reason">{check.reasonCode ? <code className="font-mono text-[13px]">{check.reasonCode}</code> : "—"}</Row>
          <Row label="Likely cause">{humanize(check.likelyFailureDomain)}</Row>
          <Row label="Next step">{humanize(check.nextAction)}</Row>
          {(check.expectedScope || check.expectedPermission) && (
            <Row label="Access it needs">
              {[check.expectedScope, check.expectedPermission].filter(Boolean).map((value) => (
                <code key={value} className="mr-2 font-mono text-[13px]">{value}</code>
              ))}
            </Row>
          )}
          {check.probeStatus && <Row label="Wallet list check">{humanize(check.probeStatus)}</Row>}
          {check.generatedAt && <Row label="Checked">{formatTime(check.generatedAt)}</Row>}
        </dl>
        {check.tools.length > 0 && (
          <div className="mt-3">
            <div className="mb-1 text-xs font-semibold text-muted">What you can do</div>
            <ul className="divide-y divide-line rounded-md border border-line">
              {check.tools.map((tool) => (
                <ToolItem key={tool.id} tool={tool} />
              ))}
            </ul>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function SignInEvents({ result, filtered }: { result: SignInDiagnostics; filtered: boolean }) {
  if (result.status === "degraded") {
    return (
      <NotAvailableState
        title="Sign-in events could not be read"
        description="The sign-in diagnostics source did not answer, so this says nothing about whether events exist. Try again shortly; if it persists, contact Alloy support."
        reason={result.reason ? `Reason: ${result.reason}` : undefined}
      />
    );
  }
  if (result.events.length === 0) {
    return (
      <div data-testid="signin-empty" className="rounded-lg border border-line bg-surface px-4 py-10 text-center text-sm text-muted">
        {filtered ? "No sign-in events match these filters." : "No recent sign-in events were recorded."}
      </div>
    );
  }
  return (
    <>
      <TableWrapper>
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg">
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold text-muted">Time</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold text-muted">Event</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold text-muted">Outcome</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold text-muted">Reason</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold text-muted">What to do</th>
              <th className="px-4 py-[9px] text-left text-[10.5px] font-semibold text-muted">Support reference</th>
            </tr>
          </thead>
          <tbody>
            {result.events.map((event) => (
              <tr key={event.id} className="border-b border-line last:border-b-0" data-testid="signin-event">
                <td className="whitespace-nowrap px-4 py-[9px] text-[12px] text-ink">{formatTime(event.occurredAt)}</td>
                <td className="px-4 py-[9px] text-[12px] text-ink">{humanize(event.eventType)}</td>
                <td className="px-4 py-[9px]">
                  <Badge variant={outcomeTone(event.outcome)} size="sm">{event.outcome ? humanize(event.outcome) : "Unknown"}</Badge>
                </td>
                <td className="px-4 py-[9px] font-mono text-[11px] text-ink">{event.reasonCode ?? "—"}</td>
                <td className="px-4 py-[9px] text-[12px] text-muted">{event.remediationHint ?? "—"}</td>
                <td className="px-4 py-[9px] text-[12px]">
                  {event.supportReference ? (
                    <Link
                      to={`/admin/trace-lookup?ref=${encodeURIComponent(event.supportReference)}`}
                      className="font-mono text-primary hover:underline"
                    >
                      {event.supportReference}
                    </Link>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
      {result.dataBoundary && <p className="mt-2 text-xs text-muted">{result.dataBoundary}</p>}
    </>
  );
}

export default function AdminAuthDiagnosticsPage() {
  const [check, setCheck] = useState<Load<WalletAccessCheck | null>>({ kind: "loading" });
  const [events, setEvents] = useState<Load<SignInDiagnostics>>({ kind: "loading" });
  const [reference, setReference] = useState("");
  const [reasonCode, setReasonCode] = useState("");
  const [applied, setApplied] = useState<{ reference?: string; reasonCode?: string }>({});
  const [filterError, setFilterError] = useState<string | null>(null);

  const loadCheck = useCallback(async () => {
    setCheck({ kind: "loading" });
    const result = await fetchWalletAccessCheck();
    setCheck(result.ok ? { kind: "ready", data: result.data } : { kind: "failed", failure: result });
  }, []);

  const loadEvents = useCallback(async (filters: { reference?: string; reasonCode?: string }) => {
    setEvents({ kind: "loading" });
    const result = await searchSignInDiagnostics(filters);
    setEvents(result.ok ? { kind: "ready", data: result.data } : { kind: "failed", failure: result });
  }, []);

  useEffect(() => {
    void loadCheck();
    void loadEvents({});
  }, [loadCheck, loadEvents]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const ref = reference.trim();
    const code = reasonCode.trim();
    if ((ref && !SUPPORT_REFERENCE_INPUT.test(ref)) || (code && !REASON_INPUT.test(code))) {
      setFilterError("Use only letters, numbers, dots, dashes, underscores and colons.");
      return;
    }
    setFilterError(null);
    const filters = { reference: ref || undefined, reasonCode: code || undefined };
    setApplied(filters);
    void loadEvents(filters);
  };

  // A refusal applies to the whole page (same gate for both reads): show it once.
  const denied = check.kind === "failed" && check.failure.denial ? check.failure : events.kind === "failed" && events.failure.denial ? events.failure : null;

  return (
    <div>
      <PageHeader
        title="Sign-in diagnostics"
        subtitle="Why someone could not sign in or open their wallets, and what to do about it."
      />

      {denied ? (
        <AdminConsoleFailureState failure={denied} subject="sign-in diagnostics" />
      ) : (
        <div className="flex flex-col gap-6">
          <section aria-label="Wallet page access">
            {check.kind === "loading" && <LoadingState rows={3} />}
            {check.kind === "failed" && (
              <AdminConsoleFailureState failure={check.failure} subject="the wallet page access check" onRetry={() => void loadCheck()} />
            )}
            {check.kind === "ready" && <WalletAccessCard check={check.data} />}
          </section>

          <section aria-labelledby="signin-events-heading">
            <h2 id="signin-events-heading" className="mb-2 text-base font-bold text-ink">Sign-in events</h2>
            <form onSubmit={submit} noValidate>
              <FilterBar>
                <div className="min-w-0 flex-1 sm:min-w-[200px]">
                  <Input
                    aria-label="Support reference or correlation ID"
                    placeholder="Support reference or correlation ID"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>
                <div className="min-w-0 flex-1 sm:min-w-[180px]">
                  <Input
                    aria-label="Reason code"
                    placeholder="Reason code, e.g. AUTH_SESSION_EXPIRED"
                    value={reasonCode}
                    onChange={(e) => setReasonCode(e.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>
                <Button type="submit" variant="secondary" disabled={events.kind === "loading"}>
                  Search
                </Button>
              </FilterBar>
              {filterError && <p className="-mt-2 mb-3 text-xs text-danger">{filterError}</p>}
            </form>
            {events.kind === "loading" && <LoadingTable rows={4} cols={6} label="Loading sign-in events" />}
            {events.kind === "failed" && (
              <AdminConsoleFailureState failure={events.failure} subject="sign-in events" onRetry={() => void loadEvents(applied)} />
            )}
            {events.kind === "ready" && <SignInEvents result={events.data} filtered={!!(applied.reference || applied.reasonCode)} />}
          </section>
        </div>
      )}
    </div>
  );
}
